import React from "react";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek";
import CombinedDateTimePicker from "../../components/CustomComponents/DateTimePicker/CombinedDateTimePicker";
import {
  DiscountToggleRow,
  DiscountSection,
  DiscountRuleCard,
  DiscountChip,
  DiscountEmptyState,
} from "./discountFormUI";
import "./discountForm.css";

/**
 * Shared rich form body for Create / Update discount.
 */
function DiscountFormBody({
  themeColor,
  isDarkMode,
  discountData,
  handleInputChange,
  handleToggleChange,
  getAmountPlaceholder,
  getCurrencySymbol,
  compCurrency,
  applyDiscountToRules,
  selectedRule,
  handleRuleSelection,
  addAllItems,
  selectedCategories,
  selectedProducts,
  selectedCustomerGroups,
  removeCategory,
  removeProduct,
  removeCustomerGroup,
  setShowCategoryModal,
  setShowCustomerGroupModal,
  orderCountCompareType,
  setOrderCountCompareType,
  orderCountThreshold,
  setOrderCountThreshold,
  enableSchedule,
  setEnableSchedule,
  useSameTimeSlots,
  setUseSameTimeSlots,
  applyCommonTimeSlotToAll,
  commonTimeSlot,
  setCommonTimeSlot,
  schedules,
  setSchedules,
  enableDateRange,
  handleEnableDateRangeToggle,
  dateRange,
  handleDateRangeChange,
}) {
  return (
    <div
      className="discount-form-shell"
      style={{ "--df-accent": themeColor }}
    >
      <DiscountSection icon="fa-sliders-h" label="Basics">
        <div className="discount-basics-card">
          <div className="discount-form-fields-row mb-0">
            <div className="discount-form-field">
              <FormInput
                label="Name"
                placeHolder="Discount name"
                type="text"
                name="name"
                value={discountData.name}
                onchange={handleInputChange}
              />
              <p className="discount-field-hint">Shown on reports and at checkout</p>
            </div>
            <div className="discount-form-field">
              <FormInput
                label="Percentage"
                placeHolder={getAmountPlaceholder ? getAmountPlaceholder() : "Percentage (e.g. 10%)"}
                type="number"
                name="amount"
                value={discountData.amount}
                onchange={handleInputChange}
                min={0}
              />
              <p className="discount-field-hint">Off the eligible order total</p>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <DiscountToggleRow
            isOn={discountData.enableRules}
            title="Discount rules"
            hint="Apply this discount automatically to specific items, categories, customer groups, or order count."
          >
            <ToggleSwitch
              isChecked={discountData.enableRules}
              onToggle={(checked) => handleToggleChange(checked, "enableRules")}
              label="Discount rules"
              name="enableRules"
              themeColor={themeColor}
            />
          </DiscountToggleRow>
        </div>

        {discountData.enableRules && (
          <div className="discount-form-section--nested mt-3">
            <h3 className="discount-subsection-title">Apply discount to</h3>
            <div className="discount-rule-grid mb-3">
              {applyDiscountToRules.map((rule) => (
                <DiscountRuleCard
                  key={rule.name}
                  selected={selectedRule === rule.name}
                  icon={
                    rule.name === "Category"
                      ? "fa-tags"
                      : rule.name === "Order count"
                        ? "fa-hashtag"
                        : "fa-users"
                  }
                  title={rule.name}
                  description={rule.description}
                  onSelect={() => handleRuleSelection(rule.name)}
                />
              ))}
            </div>

            {selectedRule === "Category" && (
              <>
                <h3 className="discount-subsection-title">Selected categories *</h3>
                {!addAllItems && (
                  <div className="mb-3">
                    {selectedCategories.length > 0 ? (
                      <>
                        <div className="discount-chip-grid">
                          {selectedCategories.map((category) => (
                            <DiscountChip
                              key={category.rootCat_ID}
                              icon="fa-tag"
                              name={category.rootCat_Name}
                              onRemove={() => removeCategory(category.rootCat_ID)}
                            />
                          ))}
                        </div>
                        <div className="d-flex justify-content-center mt-3">
                          <button
                            type="button"
                            className="discount-btn-accent"
                            onClick={() => setShowCategoryModal(true)}
                          >
                            <i className="fa-solid fa-plus" aria-hidden="true" />
                            Add more categories
                          </button>
                        </div>
                      </>
                    ) : (
                      <DiscountEmptyState
                        icon="fa-tags"
                        text="No categories selected"
                        actionLabel="Add category"
                        onAction={() => setShowCategoryModal(true)}
                      />
                    )}
                  </div>
                )}

                {selectedProducts.length > 0 && !addAllItems && (
                  <div className="mt-2">
                    <h3 className="discount-subsection-title">Selected products</h3>
                    <div className="discount-chip-grid">
                      {selectedProducts.map((product) => (
                        <DiscountChip
                          key={product.menuItem_ID}
                          icon="fa-box"
                          name={product.menuItem_Name}
                          imageSrc={product.menuItem_Image || undefined}
                          onRemove={() => removeProduct(product.menuItem_ID)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {addAllItems && (
                  <p className="discount-field-hint mb-0">
                    Discount will be applied to all items.
                  </p>
                )}
              </>
            )}

            {selectedRule === "Customer group" && (
              <>
                <h3 className="discount-subsection-title">Selected customer groups *</h3>
                <div className="mb-3">
                  {selectedCustomerGroups.length > 0 ? (
                    <>
                      <div className="discount-chip-grid">
                        {selectedCustomerGroups.map((group) => (
                          <DiscountChip
                            key={group.id}
                            icon="fa-users"
                            name={group.name}
                            onRemove={() => removeCustomerGroup(group.id)}
                          />
                        ))}
                      </div>
                      <div className="d-flex justify-content-center mt-3">
                        <button
                          type="button"
                          className="discount-btn-accent"
                          onClick={() => setShowCustomerGroupModal(true)}
                        >
                          <i className="fa-solid fa-plus" aria-hidden="true" />
                          Add more customer groups
                        </button>
                      </div>
                    </>
                  ) : (
                    <DiscountEmptyState
                      icon="fa-users"
                      text="No customer groups selected"
                      actionLabel="Add customer groups"
                      onAction={() => setShowCustomerGroupModal(true)}
                    />
                  )}
                </div>

                <h3 className="discount-subsection-title">Minimum spend</h3>
                <DiscountToggleRow
                  isOn={discountData.enableMinSpend}
                  title="Set minimum spend"
                  hint={`Require a minimum subtotal (e.g. ${getCurrencySymbol(compCurrency)}50 for 10% off)`}
                  footer={
                    discountData.enableMinSpend ? (
                      <div className="discount-form-field discount-form-field--compact">
                        <FormInput
                          label="Minimum subtotal"
                          placeHolder={`${getCurrencySymbol(compCurrency)}0.00`}
                          type="number"
                          name="minSubtotal"
                          value={discountData.minSubtotal}
                          onchange={handleInputChange}
                          prepend={getCurrencySymbol(compCurrency)}
                        />
                      </div>
                    ) : null
                  }
                >
                  <ToggleSwitch
                    isChecked={discountData.enableMinSpend}
                    onToggle={(checked) => handleToggleChange(checked, "enableMinSpend")}
                    label="Set minimum spend"
                    name="enableMinSpend"
                    themeColor={themeColor}
                  />
                </DiscountToggleRow>
              </>
            )}

            {selectedRule === "Order count" && (
              <>
                <h3 className="discount-subsection-title">Order count rule *</h3>
                <div className="discount-form-fields-row mb-3">
                  <div className="discount-form-field">
                    <label className="form-label" htmlFor="orderCountCompareType">
                      Compare
                    </label>
                    <select
                      id="orderCountCompareType"
                      className="form-select"
                      value={orderCountCompareType || "Equals"}
                      onChange={(e) =>
                        setOrderCountCompareType && setOrderCountCompareType(e.target.value)
                      }
                    >
                      <option value="Equals">Equals</option>
                      <option value="AtMost">At most</option>
                    </select>
                    <p className="discount-field-hint">
                      Equals = exactly N orders; At most = 0 through N orders
                    </p>
                  </div>
                  <div className="discount-form-field">
                    <FormInput
                      label="Number of orders"
                      placeHolder="e.g. 0 for first order"
                      type="number"
                      name="orderCountThreshold"
                      value={orderCountThreshold ?? 0}
                      onchange={(e) => {
                        const parsed = parseInt(e.target.value, 10);
                        setOrderCountThreshold &&
                          setOrderCountThreshold(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
                      }}
                      min={0}
                    />
                    <p className="discount-field-hint">
                      Based on the customer’s completed order count
                    </p>
                  </div>
                </div>

                <h3 className="discount-subsection-title">Minimum spend</h3>
                <DiscountToggleRow
                  isOn={discountData.enableMinSpend}
                  title="Set minimum spend"
                  hint={`Require a minimum subtotal (e.g. ${getCurrencySymbol(compCurrency)}50 for 10% off)`}
                  footer={
                    discountData.enableMinSpend ? (
                      <div className="discount-form-field discount-form-field--compact">
                        <FormInput
                          label="Minimum subtotal"
                          placeHolder={`${getCurrencySymbol(compCurrency)}0.00`}
                          type="number"
                          name="minSubtotal"
                          value={discountData.minSubtotal}
                          onchange={handleInputChange}
                          prepend={getCurrencySymbol(compCurrency)}
                        />
                      </div>
                    ) : null
                  }
                >
                  <ToggleSwitch
                    isChecked={discountData.enableMinSpend}
                    onToggle={(checked) => handleToggleChange(checked, "enableMinSpend")}
                    label="Set minimum spend"
                    name="enableMinSpend"
                    themeColor={themeColor}
                  />
                </DiscountToggleRow>
              </>
            )}
          </div>
        )}
      </DiscountSection>

      <DiscountSection icon="fa-calendar-alt" label="Discount schedule">
        <div className="discount-options-stack">
          <DiscountToggleRow
            isOn={enableSchedule}
            title="Set schedule"
            hint="Set the days and times this discount is available (e.g. Happy Hour)"
            footer={
              enableSchedule ? (
                <div className="discount-schedule-body">
                  <DiscountToggleRow
                    isOn={useSameTimeSlots}
                    title="Use same time slots for all days"
                    hint="Apply one time window across every day of the week"
                  >
                    <ToggleSwitch
                      isChecked={useSameTimeSlots}
                      onToggle={() => {
                        setUseSameTimeSlots(!useSameTimeSlots);
                        if (!useSameTimeSlots) {
                          applyCommonTimeSlotToAll();
                        }
                      }}
                      label="Use same time slots for all days"
                      disabled={false}
                      themeColor={themeColor}
                    />
                  </DiscountToggleRow>

                  <div className="mt-3">
                    {useSameTimeSlots ? (
                      <CommonTimeSlotForAllWeek
                        setCommonTimeSlots={setCommonTimeSlot}
                        commonTimeSlots={commonTimeSlot}
                        isProfilePage={false}
                      />
                    ) : (
                      <ScheduleManager
                        schedules={schedules}
                        setSchedules={setSchedules}
                        isProfilePage={false}
                        isDarkMode={isDarkMode}
                      />
                    )}
                  </div>
                </div>
              ) : null
            }
          >
            <ToggleSwitch
              isChecked={enableSchedule}
              onToggle={() => setEnableSchedule(!enableSchedule)}
              label="Set schedule"
              name="enableSchedule"
              themeColor={themeColor}
            />
          </DiscountToggleRow>

          <DiscountToggleRow
            isOn={enableDateRange}
            title="Set date range"
            hint="Set the dates the discount is available (e.g. Seasonal Sale)"
            footer={
              enableDateRange ? (
                <div className="discount-form-fields-row mb-0">
                  <div className="discount-form-field">
                    <CombinedDateTimePicker
                      label="Start date & time"
                      value={dateRange.startDateTime}
                      onChange={(date) => handleDateRangeChange("startDateTime", date)}
                      ampm={true}
                      placeholder="Select start date and time"
                      name="startDateTime"
                    />
                  </div>
                  <div className="discount-form-field">
                    <CombinedDateTimePicker
                      label="End date & time"
                      value={dateRange.endDateTime}
                      onChange={(date) => handleDateRangeChange("endDateTime", date)}
                      ampm={true}
                      placeholder="Select end date and time"
                      name="endDateTime"
                    />
                  </div>
                </div>
              ) : null
            }
          >
            <ToggleSwitch
              isChecked={enableDateRange}
              onToggle={handleEnableDateRangeToggle}
              label="Set date range"
              name="enableDateRange"
              themeColor={themeColor}
            />
          </DiscountToggleRow>
        </div>
      </DiscountSection>

      <DiscountSection icon="fa-cog" label="Options">
        <div className="discount-options-stack">
          <DiscountToggleRow
            isOn={discountData.enableMaxDiscount}
            title="Set maximum discount value"
            hint="Maximum discount value per purchase. Applies to percentage-based discounts only."
            footer={
              discountData.enableMaxDiscount ? (
                <div className="discount-form-field discount-form-field--compact">
                  <FormInput
                    label={`Maximum discount value (${getCurrencySymbol(compCurrency)})`}
                    placeHolder={`${getCurrencySymbol(compCurrency)}0.00`}
                    type="number"
                    name="maxDiscountValue"
                    value={discountData.maxDiscountValue}
                    onchange={handleInputChange}
                  />
                </div>
              ) : null
            }
          >
            <ToggleSwitch
              isChecked={discountData.enableMaxDiscount}
              onToggle={(checked) => handleToggleChange(checked, "enableMaxDiscount")}
              label="Set maximum discount value"
              name="enableMaxDiscount"
              themeColor={themeColor}
            />
          </DiscountToggleRow>

          <DiscountToggleRow
            isOn={discountData.enableMinQuantity}
            title="Set minimum quantity"
            hint="Minimum quantity required for the discount to apply"
            footer={
              discountData.enableMinQuantity ? (
                <div className="discount-form-field discount-form-field--compact">
                  <FormInput
                    label="Minimum quantity"
                    placeHolder="Minimum quantity"
                    type="number"
                    name="quantity"
                    value={discountData.quantity}
                    onchange={handleInputChange}
                  />
                </div>
              ) : null
            }
          >
            <ToggleSwitch
              isChecked={!!discountData.enableMinQuantity}
              onToggle={(checked) => handleToggleChange(checked, "enableMinQuantity")}
              label="Set minimum quantity"
              name="enableMinQuantity"
              themeColor={themeColor}
            />
          </DiscountToggleRow>

          <div className="discount-options-grid">
            <DiscountToggleRow
              isOn={discountData.requirePasscode}
              title="Require passcode"
              hint="Passcode required at checkout"
            >
              <ToggleSwitch
                isChecked={discountData.requirePasscode}
                onToggle={(checked) => handleToggleChange(checked, "requirePasscode")}
                label="Require passcode"
                name="requirePasscode"
                themeColor={themeColor}
              />
            </DiscountToggleRow>
            <DiscountToggleRow
              isOn={discountData.applyDiscountAfterTaxes}
              title="Apply discount after taxes"
              hint="For manufacturer discounts / instant rebates"
            >
              <ToggleSwitch
                isChecked={discountData.applyDiscountAfterTaxes}
                onToggle={(checked) =>
                  handleToggleChange(checked, "applyDiscountAfterTaxes")
                }
                label="Apply discount after taxes"
                name="applyDiscountAfterTaxes"
                themeColor={themeColor}
              />
            </DiscountToggleRow>
          </div>
        </div>
      </DiscountSection>
    </div>
  );
}

export default DiscountFormBody;
